import { Dropdown, SwitchToggle, TextHeader, useSnackbar } from '@Iot-Bee/standard-web-library';
import { Box } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import ExportIcon from './ExportIcon';
import { BarChart } from '../charts/BarChart';
import { GroupBy, groupByOptions, TimeGroupingInterval, timePeriodOptions, TimeRange } from '../../types';
import { DashboardDeviceData } from '../../interfaces';
import {
  adjustTempDataToKWHDates,
  fetchWeatherData,
  generateWeatherAPIURL,
  loadSessionStorageValues,
} from './utils';
import { DenmarkArea, denmarkAreas } from './types';

import { getBarchartData } from '../../util';
import { Dayjs } from 'dayjs';

interface BarchartConsumptionProps {
  data: DashboardDeviceData[];
  timeRange: TimeRange;
  isLoading: boolean;
  friendlyNameMap: Map<string, string>;
  fromDate: Dayjs;
  toDate: Dayjs;
}

const BarchartConsumption: FC<BarchartConsumptionProps> = ({
  data,
  timeRange,
  isLoading,
  friendlyNameMap,
  fromDate,
  toDate,
}) => {
  const [timeGrouping, setTimeGrouping] = useState<TimeGroupingInterval>('day');
  const [groupBy, setGroupBy] = useState<GroupBy>('Working Hours');
  const [showTemperature, setShowTemperature] = useState<boolean>(false);
  const [selectedTemperatureArea, setSelectedTemperatureArea] = useState<DenmarkArea>('Hovedstaden');

  const [sessionStorageLoaded, setSessionStorageLoaded] = useState<boolean>(false);

  const [weatherData, setWeatherData] = useState<number[]>([]);
  const [weatherLoaded, setWeatherLoaded] = useState<boolean>(false);

  const barchartData = useMemo(() => {
    if (sessionStorageLoaded) {
      return getBarchartData(groupBy, data, timeRange, timeGrouping);
    } else {
      return { categories: [], series: [] };
    }
  }, [data, timeRange, timeGrouping, groupBy, sessionStorageLoaded]);

  const {
    actions: { openSnackbar },
  } = useSnackbar();

  useEffect(() => {
    if (!showTemperature) return;

    const url = generateWeatherAPIURL(selectedTemperatureArea, fromDate, toDate);

    setWeatherLoaded(false);
    fetchWeatherData(url, (type, message) => openSnackbar(type, message))
      .then((data) => {
        if (data) {
          const dateTempDict = adjustTempDataToKWHDates(data, barchartData, timeGrouping);
          const tempValues = Object.values(dateTempDict);
          // const { insideValues, outsideValues } = extractInsideOutsideValues(dateTempDict);
          setWeatherData(tempValues);
        }
      })
      .catch((e) => {
        console.error('error getting weather data', e);
      })
      .finally(() => {
        setWeatherLoaded(true);
      });
  }, [fromDate, toDate, timeGrouping, showTemperature, barchartData, selectedTemperatureArea]);

  useEffect(() => {
    const { timePeriod, groupBy, showTemperature, denmarkArea, sessionStorageLoaded } = loadSessionStorageValues();

    if (timePeriod) setTimeGrouping(timePeriod);
    if (groupBy) setGroupBy(groupBy);
    if (showTemperature !== null) setShowTemperature(showTemperature);
    if (denmarkArea) setSelectedTemperatureArea(denmarkArea);

    setSessionStorageLoaded(sessionStorageLoaded);
  }, []);

  const handleTimePeriodChange = (period: TimeGroupingInterval) => {
    setTimeGrouping(period);
    sessionStorage.setItem('timePeriod', period);
  };

  const handleGroupByChange = (groupBy: GroupBy) => {
    setGroupBy(groupBy);
    sessionStorage.setItem('groupBy', groupBy);
  };

  const handleShowTemperatureChange = (show: boolean) => {
    setWeatherLoaded(false);
    setShowTemperature(show);
    sessionStorage.setItem('showTemperature', JSON.stringify(show));
  };

  const handleChangeArea = (area: DenmarkArea) => {
    setSelectedTemperatureArea(area as DenmarkArea);
    sessionStorage.setItem('denmarkArea', area);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', backgroundColor: 'rgba(12, 92, 248, 0.1)', padding: '5px' }}>
        <Box sx={{ display: 'flex', width: '99%', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Dropdown
              label=""
              placeholder="Time Period"
              options={[...timePeriodOptions]}
              value={timeGrouping}
              onChange={(event) => {
                const timePeriod = event.target.value as TimeGroupingInterval;
                handleTimePeriodChange(timePeriod);
              }}
            />
            <Dropdown
              label=""
              placeholder="Group by"
              options={[...groupByOptions]}
              value={groupBy}
              onChange={(event) => {
                const groupBy = event.target.value as GroupBy;
                handleGroupByChange(groupBy);
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {showTemperature && (
              <Dropdown
                sx={{ minWidth: 'fit-content' }}
                label=""
                placeholder="Area"
                options={[...denmarkAreas]}
                value={selectedTemperatureArea}
                onChange={(event) => {
                  const area = event.target.value;
                  handleChangeArea(area as DenmarkArea);
                }}
              />
            )}
            <Box
              sx={{
                marginLeft: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '5px',
                width: 'fit-content',
              }}
            >
              <TextHeader>Show Outside Temperature: </TextHeader>
              <SwitchToggle
                sx={{ marginLeft: '10px' }}
                name="show temperature"
                checked={showTemperature}
                onChange={(e) => {
                  const checked = e.target.checked;
                  handleShowTemperatureChange(checked);
                }}
              />
            </Box>
            <ExportIcon
              data={barchartData}
              selectedDevices={data.map((d) => friendlyNameMap.get(d.sensorId) ?? d.sensorId)}
              workingHoursRange={[300, 1500]}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ height: '100%' }}>
        <BarChart
          barchartData={barchartData}
          groupBy={groupBy}
          isLoading={isLoading}
          showTemperature={showTemperature}
          temperatureData={weatherLoaded ? weatherData : []}
        />
      </Box>
    </Box>
  );
};

export default BarchartConsumption;
